import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useApi } from '../../../lib/useApi';
import { useLlmJobsPoll } from '../../../lib/useLlmJobsPoll';
import type { CraftCategoryRecord, CraftModuleRecord, CraftLearningPath, UserCraftProgress } from '../../../types/craft';
import CraftLibraryHome from './CraftLibraryHome';
import CraftModuleViewer from './CraftModuleViewer';
import CraftLearningPaths from './CraftLearningPaths';
import CraftProgressDashboard from './CraftProgressDashboard';

type SubTab = 'library' | 'paths' | 'progress';

export default function MyCraft() {
  const { apiGet, apiPost } = useApi();
  const { user, membership, organisation } = useAuth();
  const [subTab, setSubTab] = useState<SubTab>('library');
  const [categories, setCategories] = useState<CraftCategoryRecord[]>([]);
  const [modules, setModules] = useState<CraftModuleRecord[]>([]);
  const [paths, setPaths] = useState<CraftLearningPath[]>([]);
  const [progress, setProgress] = useState<UserCraftProgress[]>([]);
  const [activeModuleSlug, setActiveModuleSlug] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoGenTriggered = useRef(false);

  const isAdmin = membership?.role === 'admin';

  // Drive the craft-module background run (deep research) to finalization while any module is
  // processing. The module-list poll below observes the resulting status flip.
  useLlmJobsPoll(
    organisation?.id,
    modules.some(m => m.status === 'processing'),
    '/v1/craft/llm-jobs/check',
  );

  const loadData = useCallback(async () => {
    const [catRes, modRes, pathRes] = await Promise.all([
      apiGet<{ categories: CraftCategoryRecord[] }>('/v1/craft-categories'),
      apiGet<{ modules: CraftModuleRecord[] }>('/v1/craft-modules'),
      apiGet<{ paths: CraftLearningPath[] }>('/v1/craft-learning-paths'),
    ]);
    setCategories(catRes.categories || []);
    setModules(modRes.modules || []);
    setPaths(pathRes.paths || []);
    setLoading(false);
  }, [apiGet]);

  const loadProgress = useCallback(async () => {
    if (!user) return;
    const { progress: data } = await apiGet<{ progress: UserCraftProgress[] }>('/v1/user-craft-progress');
    setProgress(data || []);
  }, [apiGet, user]);

  useEffect(() => {
    loadData();
    loadProgress();
  }, [loadData, loadProgress]);

  useEffect(() => {
    const hasProcessing = modules.some(m => m.status === 'processing');
    if (hasProcessing) {
      pollingRef.current = setInterval(async () => {
        const { modules: data } = await apiGet<{ modules: CraftModuleRecord[] }>('/v1/craft-modules');
        if (data) {
          setModules(data);
          if (!data.some(m => m.status === 'processing') && pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
        }
      }, 5000);
    }
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [apiGet, modules]);

  useEffect(() => {
    if (loading || autoGenTriggered.current || !isAdmin) return;
    const ungenerated = modules.filter(m => m.status === 'none');
    if (ungenerated.length === 0 || modules.length === 0) return;
    autoGenTriggered.current = true;
    ungenerated.forEach((mod, idx) => {
      setTimeout(() => triggerGenerate(mod.slug), 2000 + idx * 3000);
    });
  }, [loading, modules, isAdmin]);

  const triggerGenerate = async (moduleSlug: string) => {
    try {
      await apiPost('/v1/generate-craft-module', { module_slug: moduleSlug });
      setModules(prev =>
        prev.map(m =>
          m.slug === moduleSlug ? { ...m, status: 'processing' as const, current_stage: 0 } : m
        )
      );
    } catch {
      // silent
    }
  };

  if (activeModuleSlug) {
    return (
      <CraftModuleViewer
        moduleSlug={activeModuleSlug}
        progress={progress}
        onBack={() => setActiveModuleSlug(null)}
        onProgressUpdate={loadProgress}
      />
    );
  }

  return (
    <div>
      <div className="flex gap-1 mb-6 border-b border-app-line">
        {([
          { id: 'library' as const, label: 'Library' },
          { id: 'paths' as const, label: 'Learning Paths' },
          { id: 'progress' as const, label: 'My Progress' },
        ]).map(tab => (
          <button
            key={tab.id}
            onClick={() => setSubTab(tab.id)}
            className={`px-4 py-2.5 text-xs font-medium border-b-2 -mb-px transition-colors ${
              subTab === tab.id
                ? 'border-forest-600 text-forest-700'
                : 'border-transparent text-app-muted hover:text-app-muted'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {subTab === 'library' && (
        <CraftLibraryHome
          categories={categories}
          modules={modules}
          progress={progress}
          loading={loading}
          isAdmin={isAdmin}
          onSelectModule={setActiveModuleSlug}
          onGenerate={triggerGenerate}
        />
      )}

      {subTab === 'paths' && (
        <CraftLearningPaths
          paths={paths}
          modules={modules}
          progress={progress}
          onSelectModule={setActiveModuleSlug}
        />
      )}

      {subTab === 'progress' && (
        <CraftProgressDashboard
          modules={modules}
          progress={progress}
          onSelectModule={setActiveModuleSlug}
        />
      )}
    </div>
  );
}
